import { useCallback, useMemo } from 'react';

import type {
  IIndirectTaxJurisdiction,
  IIndirectTaxSupportedRegion,
} from './types';
import { useAllIndirectTaxRegistrationJurisdictions } from './useIndirectTaxRegistrationJurisdictions';

/** Resolves all jurisdictions a supported region covers, across both jurisdictionFilterSets. */
export function useIndirectTaxRegionJurisdictions({
  corporationId,
  region,
  enabled,
}: {
  corporationId: number;
  region: IIndirectTaxSupportedRegion | null;
  enabled: boolean;
}) {
  const first = useAllIndirectTaxRegistrationJurisdictions(
    corporationId,
    enabled ? (region?.jurisdictionFilterSets[0] ?? null) : null
  );
  const second = useAllIndirectTaxRegistrationJurisdictions(
    corporationId,
    enabled ? (region?.jurisdictionFilterSets[1] ?? null) : null
  );

  const jurisdictions = useMemo<IIndirectTaxJurisdiction[]>(
    () => [...first.jurisdictions, ...second.jurisdictions],
    [first.jurisdictions, second.jurisdictions]
  );

  const jurisdictionIds = useMemo(
    () => Array.from(new Set(jurisdictions.map((j) => j.jurisdictionId))),
    [jurisdictions]
  );

  const retry = useCallback(
    () => Promise.all([first.mutate(), second.mutate()]),
    [first.mutate, second.mutate]
  );

  return {
    jurisdictions,
    jurisdictionIds,
    isLoading: first.isLoading || second.isLoading,
    error: first.error ?? second.error ?? null,
    retry,
  };
}
